import React, { useMemo, useEffect, useState } from "react";
import { StepUpSpinner } from "../../Components/Loader";
import {
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  IconButton,
  FormControl,
  OutlinedInput,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Autocomplete,
  TableBody,
  Stack,
} from "@mui/material";
import { useFormik } from "formik";
import { uploadImage } from "../../Components/UploadFile/upload";
import { useStyles } from "../../theme/appStyles";
import { useNotifier } from "../../Core/Notifier";
import {
  ICompanyDetails,
  punchSlideItems,
} from "../../Api/Interface/api.interface";
import {
  useCreateSettingMutation,
  useGetSettingQuery,
} from "../../Api/attoDeskApi";
import SaveIcon from "@mui/icons-material/Save";
import {
  formatPhoneNumberForDisplay,
  validatePhoneNumber,
} from "../../Components/Validation/validationUtils";
import { toProperCase } from "../../Components/Validation/toProperCase";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { FaTrash } from "react-icons/fa";
import { punchSliderItems } from "../../Core/Enum/enum";
import useCompany from "../../Core/hook/useCompany";
import LogoPreview from "../../Components/LogoPreview/LogoPreview";

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const { refresh: refreshCompany } = useCompany();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [selectedImageUp, setSelectedImageUp] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading2, setIsImageUploadingUp] = useState(false);

  const [selectedImagePunch, setSelectedImagePunch] = useState<
    File | undefined
  >(undefined);
  const [isImageUploadingPunch, setIsImageUploadingPunch] = useState(false);

  // Email Logo — separate from companyLogo. `selectedEmailLogo` is the
  // File the user just picked (not yet uploaded); `emailLogoPath` is the
  // relative storage path returned by uploadImage, which is what the
  // backend stores in settings.email_logo_path.
  const [selectedEmailLogo, setSelectedEmailLogo] = useState<File | undefined>(undefined);
  const [emailLogoPath, setEmailLogoPath] = useState<string>("");
  const [isEmailLogoUploading, setIsEmailLogoUploading] = useState(false);

  // Favicon upload — pattern differs from the primary logo in two ways:
  //   1. NO client-side resize: a favicon should be delivered at its full
  //      recommended 512×512 so the browser can pick the sharpest bitmap.
  //      Resizing to 500×500 (the primary-logo resize target) would
  //      destroy the intended crispness.
  //   2. Upload happens on file select (not deferred to Save) so the
  //      LogoPreview + saved `favicon_path` field stay in sync with what
  //      the storage layer actually holds.
  const [isFaviconUploading, setIsFaviconUploading] = useState(false);

  const [punchSlideItems, setPunchSlideItems] = useState<punchSlideItems[]>([]);

  const { data, isLoading, isError } = useGetSettingQuery({
    searchText: "",
    id: "1",
  });

  const [settingData, { isLoading: settingLoading }] =
    useCreateSettingMutation();
  const [initialData, setInitialData] = useState<ICompanyDetails | null>(null);

  useEffect(() => {
    if (data?.data) {
      setInitialData(data.data as ICompanyDetails);
    }
  }, [data]);

  useEffect(() => {
    if (initialData && Array.isArray(initialData.punch_slider_items)) {
      setPunchSlideItems(initialData.punch_slider_items);
    } else {
      setPunchSlideItems([]);
    }
  }, [initialData]);

  // Seed the emailLogoPath from the persisted value so a save without
  // picking a new file keeps the previously uploaded image attached.
  useEffect(() => {
    if (initialData && (initialData as any).email_logo_path) {
      setEmailLogoPath(String((initialData as any).email_logo_path));
    }
  }, [initialData]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik({
    initialValues: {
      companyName: initialData?.companyName || "",
      companyProvince: initialData?.companyProvince || "",
      companyCity: initialData?.companyCity || "",
      companyPostalCode: initialData?.companyPostalCode || "",
      companyStreetNumber: initialData?.companyStreetNumber || "",
      companyStreetName: initialData?.companyStreetName || "",
      companyPaymentMethod: initialData?.companyPaymentMethod || "",
      companyEmail: initialData?.companyEmail || "",

      companyMobileNumber: initialData?.companyMobileNumber
        ? formatPhoneNumberForDisplay(initialData.companyMobileNumber)
        : "",

      companyAltMobileNumber: initialData?.companyAltMobileNumber
        ? formatPhoneNumberForDisplay(initialData.companyAltMobileNumber)
        : "",
      companyWebSiteLink: initialData?.companyWebSiteLink || "",
      companyDescription: initialData?.companyDescription || "",
      companyLogo: initialData?.companyLogo || "",
      logowidthPrint: initialData?.logowidthPrint || "",
      backgroundImg: initialData?.backgroundImg || "",
      // Email Logo (separate from companyLogo + print-logo width). One of
      // upload | url drives which of the two below is authoritative.
      email_logo_source_type: initialData?.email_logo_source_type || "upload",
      email_logo_path: initialData?.email_logo_path || "",
      email_logo_url: initialData?.email_logo_url || "",
      email_logo_width: initialData?.email_logo_width || 220,
      punch_login_background_color:
        initialData?.punch_login_background_color || "",
      punch_login_background_url: initialData?.punch_login_background_url || "",
      punch_login_font_color: initialData?.punch_login_font_color || "",
      punch_slider_items: initialData?.punch_slider_items || [],
      calculate_radius: initialData?.calculate_radius || "",
      company_latitude: initialData?.company_latitude || "",
      company_longitude: initialData?.company_longitude || "",
      // White-label branding fields (2026_09_08 migration).
      company_short_name:    initialData?.company_short_name    || "",
      company_legal_name:    initialData?.company_legal_name    || "",
      company_country:       initialData?.company_country       || "",
      companyAltEmail:       initialData?.companyAltEmail       || "",
      email_from_name:       initialData?.email_from_name       || "",
      email_footer_text:     initialData?.email_footer_text     || "",
      favicon_path:          initialData?.favicon_path          || "",
      login_logo_path:       initialData?.login_logo_path       || "",
      portal_background_url: initialData?.portal_background_url || "",
      print_logo_path:       initialData?.print_logo_path       || "",
      secondary_logo_path:   initialData?.secondary_logo_path   || "",
      brand_primary_color:   initialData?.brand_primary_color   || "",
      brand_secondary_color: initialData?.brand_secondary_color || "",
      brand_accent_color:    initialData?.brand_accent_color    || "",
      facebook_url:          initialData?.facebook_url          || "",
      instagram_url:         initialData?.instagram_url         || "",
      linkedin_url:          initialData?.linkedin_url          || "",
      youtube_url:           initialData?.youtube_url           || "",
      tiktok_url:            initialData?.tiktok_url            || "",
      twitter_url:           initialData?.twitter_url           || "",
      // Admin notification recipients (2026_09_09). Chip input below;
      // stored as a lowercased-and-deduplicated string[] on the server.
      admin_notification_emails:
        Array.isArray(initialData?.admin_notification_emails)
          ? (initialData!.admin_notification_emails as string[])
          : [],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const companyMobileNumberError = validatePhoneNumber(
        values.companyMobileNumber
      );
      if (companyMobileNumberError) {
        errors.companyMobileNumber = companyMobileNumberError;
      }

      const companyAltMobileNumberError = validatePhoneNumber(
        values.companyAltMobileNumber
      );
      if (companyAltMobileNumberError) {
        errors.companyAltMobileNumber = companyAltMobileNumberError;
      }

      // Admin notification emails — validate each chip individually so a
      // bad one blocks the save with a clear message rather than the
      // server bouncing the entire form. Matches server rule (email:filter).
      const list = (values as any).admin_notification_emails as string[] | undefined;
      if (Array.isArray(list)) {
        if (list.length > 20) {
          errors.admin_notification_emails = "Maximum 20 admin notification email addresses.";
        } else {
          // eslint-disable-next-line no-useless-escape
          const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const bad = list.find((e) => !re.test(String(e).trim()));
          if (bad) errors.admin_notification_emails = `"${bad}" is not a valid email address.`;
        }
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let companyLogo: string | undefined = initialData?.companyLogo;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("companyLogo", selectedImage);
          companyLogo = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const formDataUp = new FormData();
        let backgroundImg: string | undefined = initialData?.backgroundImg;

        if (selectedImageUp) {
          setIsImageUploadingUp(true);
          formDataUp.append("backgroundImg", selectedImageUp);
          backgroundImg = (await uploadImage(selectedImageUp)) || "";
          setIsImageUploadingUp(false);
        }

        // Email Logo: only upload if the user actually picked a new file
        // this session; otherwise keep the already-stored path from
        // initialData. URL-mode saves the raw string directly.
        if (selectedEmailLogo) {
          setIsEmailLogoUploading(true);
          const uploaded = (await uploadImage(selectedEmailLogo)) || "";
          if (uploaded) setEmailLogoPath(uploaded);
          setIsEmailLogoUploading(false);
        }

        const formDataPunch = new FormData();
        let punch_login_background_url: string | undefined =
          initialData?.punch_login_background_url;

        if (selectedImagePunch) {
          setIsImageUploadingPunch(true);
          formDataPunch.append(
            "punch_login_background_url",
            selectedImagePunch
          );
          punch_login_background_url =
            (await uploadImage(selectedImagePunch)) || "";
          setIsImageUploadingPunch(false);
        }

        const updatedData = {
          id: initialData?.id,
          companyName: toProperCase(values.companyName),
          companyProvince: toProperCase(values.companyProvince),
          companyCity: toProperCase(values.companyCity),
          companyPostalCode: toAllCaps(values.companyPostalCode),
          companyStreetNumber: values.companyStreetNumber,
          companyStreetName: toProperCase(values.companyStreetName),
          companyPaymentMethod: values.companyPaymentMethod,
          companyEmail: values.companyEmail,
          companyMobileNumber: values.companyMobileNumber.replace(/-/g, ""),
          companyAltMobileNumber: values.companyAltMobileNumber.replace(
            /-/g,
            ""
          ),
          companyWebSiteLink: values.companyWebSiteLink,
          companyDescription: values.companyDescription,
          companyLogo,
          logowidthPrint: values.logowidthPrint,
          backgroundImg,
          // Email Logo — send only the type that actually has a value so
          // an accidentally-blank field never nulls the other one.
          email_logo_source_type: values.email_logo_source_type || null,
          email_logo_path: values.email_logo_source_type === "upload" ? (emailLogoPath || null) : null,
          email_logo_url: values.email_logo_source_type === "url" ? (values.email_logo_url || null) : null,
          email_logo_width: Number(values.email_logo_width) || 220,
          punch_login_background_color: values.punch_login_background_color,
          punch_login_font_color: values.punch_login_font_color,
          punch_login_background_url,
          punch_slider_items: punchSlideItems,
          calculate_radius: values.calculate_radius,
          company_latitude: values.company_latitude,
          company_longitude: values.company_longitude,
          // White-label branding — send empty strings as null so the DB
          // column returns to unset rather than storing a literal "".
          company_short_name:    values.company_short_name    || null,
          company_legal_name:    values.company_legal_name    || null,
          company_country:       values.company_country       || null,
          companyAltEmail:       values.companyAltEmail       || null,
          email_from_name:       values.email_from_name       || null,
          email_footer_text:     values.email_footer_text     || null,
          favicon_path:          values.favicon_path          || null,
          login_logo_path:       values.login_logo_path       || null,
          portal_background_url: values.portal_background_url || null,
          print_logo_path:       values.print_logo_path       || null,
          secondary_logo_path:   values.secondary_logo_path   || null,
          brand_primary_color:   values.brand_primary_color   || null,
          brand_secondary_color: values.brand_secondary_color || null,
          brand_accent_color:    values.brand_accent_color    || null,
          facebook_url:          values.facebook_url          || null,
          instagram_url:         values.instagram_url         || null,
          linkedin_url:          values.linkedin_url          || null,
          youtube_url:           values.youtube_url           || null,
          tiktok_url:            values.tiktok_url            || null,
          twitter_url:           values.twitter_url           || null,
          // Admin notification recipients — send trimmed lowercased array,
          // or null when empty so the DB column returns to "unset".
          admin_notification_emails: (() => {
            const raw = (values as any).admin_notification_emails as string[] | undefined;
            if (!Array.isArray(raw) || raw.length === 0) return null;
            const seen: Record<string, true> = {};
            const out: string[] = [];
            raw.forEach((e) => {
              const norm = String(e).trim().toLowerCase();
              if (!norm || seen[norm]) return;
              seen[norm] = true;
              out.push(norm);
            });
            return out.length ? out : null;
          })(),
        };

        const response = await settingData(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          const tokenData = response.data as ICompanyDetails;
          if (tokenData) {
            localStorage.setItem("backgroundImg", tokenData.backgroundImg);
            localStorage.setItem("companyLogo", tokenData.companyLogo);
            // Live refresh — refetch /api/company/branding and update
            // Redux + document.title + favicon + brand CSS variables. No
            // page reload, no logout.
            void refreshCompany();
          }
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.companyName === "" ||
      formik.values.companyName === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const maxSize = 5 * 1024 * 1024;

      if (!file.type.startsWith("image/")) {
        showErrorMessage("The file must be an image.");
        setSelectedImage(undefined);
        return;
      }

      if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 5 MB.");
        setSelectedImage(undefined);
        return;
      }

      try {
        const resizedImage = await resizeImage(file, 500, 500);
        setSelectedImage(resizedImage);
      } catch (error) {
        showErrorMessage("Failed to resize the image.");
        setSelectedImage(undefined);
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const resizeImageUp = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile2 = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile2);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange2 = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImageUp(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 10 MB.");
        setSelectedImageUp(undefined);
      } else {
        // Background images are hero art rendered at ~60% of the viewport
        // width on desktop, so we keep the original file at full resolution.
        // The old path routed the upload through resizeImageUp(500,500) which
        // canvas-downsampled a 2048×1365 upload to ~500×333 and re-encoded it
        // lossily — that's what produced the "blurry background" complaint.
        // The 10 MB size cap above already prevents abuse.
        setSelectedImageUp(file);
      }
    } else {
      setSelectedImageUp(undefined);
    }
  };
  // Favicon upload — validates client-side (MIME + size), uploads via
  // the shared /utility/upload endpoint (which re-validates server-side
  // with Laravel's `image|mimes:...|max:2048` rule so an attacker cannot
  // bypass this by crafting a request), and writes the returned public
  // URL into formik `favicon_path` so LogoPreview and the eventual Save
  // both see the same value.
  const handleFaviconChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    // Server accepts jpeg/png/jpg/gif (see ImgUploadController). SVG is
    // intentionally excluded from the shared endpoint's mimes list, so
    // we mirror that policy here rather than uploading and then failing.
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    const maxSize = 2 * 1024 * 1024; // Matches backend max:2048.

    if (!validTypes.includes(file.type)) {
      showErrorMessage("Favicon must be a PNG, JPG, JPEG, or GIF image.");
      event.target.value = "";
      return;
    }
    if (file.size > maxSize) {
      showErrorMessage("Favicon must be smaller than 2 MB.");
      event.target.value = "";
      return;
    }

    try {
      setIsFaviconUploading(true);
      const uploadedUrl = await uploadImage(file);
      if (uploadedUrl) {
        formik.setFieldValue("favicon_path", uploadedUrl);
      } else {
        showErrorMessage("Favicon upload failed.");
      }
    } catch {
      showErrorMessage("Favicon upload failed.");
    } finally {
      setIsFaviconUploading(false);
      event.target.value = "";
    }
  };

  // Clear the stored path. The browser tab still shows the previously
  // hydrated favicon until the admin presses Save (which triggers
  // refreshCompany() and swaps the DOM <link> href back to the neutral
  // fallback via useCompany.refresh()).
  const handleFaviconRemove = () => {
    formik.setFieldValue("favicon_path", "");
  };

  const resizeImagePunch = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile2 = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile2);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChangePunch = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImagePunch(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 10 MB.");
        setSelectedImagePunch(undefined);
      } else {
        try {
          const resizeImageJJ = await resizeImagePunch(file, 500, 500);
          setSelectedImagePunch(resizeImageJJ);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImagePunch(undefined);
        }
      }
    } else {
      setSelectedImagePunch(undefined);
    }
  };

  const handleDeleteRowPB = (index: number) => {
    const updatedRows = punchSlideItems.filter((_, i) => i !== index);
    setPunchSlideItems(updatedRows);
  };

  const handleFieldChangePB = <K extends keyof punchSlideItems>(
    index: number,
    field: K,
    value: punchSlideItems[K]
  ) => {
    const updatedRows = punchSlideItems.map((term, i) => {
      if (i === index) {
        return {
          ...term,
          [field]: value !== undefined ? value : term[field],
        };
      }
      return term;
    });

    setPunchSlideItems(updatedRows);
  };

  const handleFieldChange = (
    index: number,
    field: keyof punchSlideItems,
    value: any
  ) => {
    setPunchSlideItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleFieldChangeImg = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    const maxSize = 10 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
      return;
    }

    if (file.size > maxSize) {
      showErrorMessage("The file must be smaller than 10 MB.");
      return;
    }

    const uploadedUrl = await uploadImage(file);
    if (uploadedUrl) {
      handleFieldChange(index, "url", uploadedUrl);
    }
  };

  const handleAddRowPB = () => {
    setPunchSlideItems((prev) => [
      ...prev,
      {
        type: "",
        url: "",
      },
    ]);
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
    );

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Company Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Name</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Name"
                          size="small"
                          {...formik.getFieldProps("companyName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Website Link
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Website Link"
                          size="small"
                          {...formik.getFieldProps("companyWebSiteLink")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Description</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Description"
                          size="small"
                          {...formik.getFieldProps("companyDescription")}
                          className={classes.discriptionTextField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          multiline
                          rows={8}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Contact Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Mobile Number"
                          size="small"
                          value={formik.values.companyMobileNumber}
                          onChange={(e) =>
                            handlePhoneChange(
                              "companyMobileNumber",
                              e.target.value,
                              formik.setFieldValue
                            )
                          }
                          onBlur={formik.handleBlur}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyMobileNumber &&
                            Boolean(formik.errors.companyMobileNumber)
                          }
                          helperText={
                            formik.touched.companyMobileNumber &&
                            formik.errors.companyMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Alt Mobile Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Alt Mobile Number"
                          size="small"
                          value={formik.values.companyAltMobileNumber}
                          onChange={(e) =>
                            handlePhoneChange(
                              "companyAltMobileNumber",
                              e.target.value,
                              formik.setFieldValue
                            )
                          }
                          onBlur={formik.handleBlur}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          error={
                            formik.touched.companyAltMobileNumber &&
                            Boolean(formik.errors.companyAltMobileNumber)
                          }
                          helperText={
                            formik.touched.companyAltMobileNumber &&
                            formik.errors.companyAltMobileNumber
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Email</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Email"
                          size="small"
                          {...formik.getFieldProps("companyEmail")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Address
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Street Name</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Street Name"
                          size="small"
                          {...formik.getFieldProps("companyStreetName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Street Number
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Street Number"
                          size="small"
                          {...formik.getFieldProps("companyStreetNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Postal Code</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Postal Code"
                          size="small"
                          {...formik.getFieldProps("companyPostalCode")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Province</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Province"
                          size="small"
                          {...formik.getFieldProps("companyProvince")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">City</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="City"
                          size="small"
                          {...formik.getFieldProps("companyCity")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Logo
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box className={classes.imgBox} sx={{ alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                      {/* Aspect-ratio-preserving preview. Consistent with every
                          other brand-asset preview via LogoPreview. */}
                      <LogoPreview
                        src={
                          selectedImage
                            ? URL.createObjectURL(selectedImage)
                            : (initialData?.companyLogo ?? "")
                        }
                        alt={(initialData?.companyName as string) || "Company Logo"}
                        maxWidthPx={320}
                        maxHeightPx={100}
                      />
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-1"
                          type="file"
                          onChange={handleImageChange}
                        />
                        <div style={{ textAlign: "center" }}>
                          <label htmlFor="icon-button-file-1">
                            <IconButton color="primary" aria-label="upload picture" component="span">
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>
                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                    {/* Guidance text so admins upload a well-proportioned
                        horizontal logo instead of a padded square that ends
                        up looking tiny inside the preview cell. */}
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 1.25, lineHeight: 1.6 }}>
                      Recommended logo size: 800 × 240 px. Minimum: 400 × 120 px.
                      PNG with transparent background recommended. Avoid excess
                      transparent/white padding around the logo — it makes the
                      logo appear small in every preview.
                    </Typography>
                  </Grid>

                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Logo print width
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Logo print width"
                          size="small"
                          {...formik.getFieldProps("logowidthPrint")}
                          className={classes.textField}
                          type="number"
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Add Background Image
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    {/* Larger, readable preview of the login background image.
                        Was a tiny 160px thumbnail; now 320×180 with cover-fit,
                        rounded corners and a subtle border so the admin can
                        see exactly what will render on the login screen. */}
                    <Box className={classes.imgBox}>
                      {(selectedImageUp || initialData?.backgroundImg) && (
                        <Box
                          sx={{
                            width: { xs: "100%", sm: 320 },
                            maxWidth: 320,
                            height: 180,
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid rgba(15,23,42,0.10)",
                            bgcolor: "#f8fafc",
                            mb: 1.5,
                          }}
                        >
                          <img
                            src={
                              selectedImageUp
                                ? URL.createObjectURL(selectedImageUp)
                                : initialData?.backgroundImg
                            }
                            alt="Login background preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </Box>
                      )}
                      <div>
                        <input
                          accept="image/*"
                          style={{ display: "none" }}
                          id="icon-button-file-up-bg"
                          type="file"
                          onChange={handleImageChange2}
                        />
                        <div
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <label htmlFor="icon-button-file-up-bg">
                            <IconButton
                              color="primary"
                              aria-label="upload picture up bg"
                              component="span"
                            >
                              <CloudUploadIcon sx={{ fontSize: 45 }} />
                            </IconButton>
                          </label>
                          <div>PNG, JPG and JPEG are allowed</div>
                        </div>
                      </div>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* ── Email Logo card ── Separate setting from the general
                 company logo and print-logo width. Consumed by every
                 outgoing email via emails.layouts.stepup. */}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(15,23,42,0.08)",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                }}
              >
                <Box sx={{ p: 2.5, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Email Logo</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                    Upload an image or enter a public image URL to use in all outgoing emails.
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {(["upload", "url"] as const).map((mode) => {
                      const active = (formik.values as any).email_logo_source_type === mode;
                      return (
                        <Button
                          key={mode}
                          size="small"
                          variant={active ? "contained" : "outlined"}
                          onClick={() => formik.setFieldValue("email_logo_source_type", mode)}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                        >
                          {mode === "upload" ? "Upload Image" : "Image URL"}
                        </Button>
                      );
                    })}
                  </Stack>

                  {(formik.values as any).email_logo_source_type === "url" ? (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Email Logo URL"
                        placeholder="https://example.com/stepup-email-logo.png"
                        value={(formik.values as any).email_logo_url || ""}
                        onChange={(e) => formik.setFieldValue("email_logo_url", e.target.value)}
                        error={
                          Boolean((formik.values as any).email_logo_url) &&
                          !/^https:\/\//i.test((formik.values as any).email_logo_url)
                        }
                        helperText={
                          (formik.values as any).email_logo_url &&
                          !/^https:\/\//i.test((formik.values as any).email_logo_url)
                            ? "Must be a complete https:// URL (localhost is not accepted)."
                            : " "
                        }
                      />
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <input
                        accept="image/png,image/jpeg,image/jpg"
                        id="email-logo-input"
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 5 * 1024 * 1024) {
                            showErrorMessage("Email logo must be smaller than 5 MB.");
                            return;
                          }
                          setSelectedEmailLogo(f);
                        }}
                      />
                      <label htmlFor="email-logo-input">
                        <Button
                          component="span"
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
                        >
                          Browse Image
                        </Button>
                      </label>
                      {(selectedEmailLogo || emailLogoPath) && (
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedEmailLogo(undefined);
                            setEmailLogoPath("");
                          }}
                          sx={{ ml: 1, textTransform: "none", color: "#b91c1c" }}
                        >
                          Remove Logo
                        </Button>
                      )}
                      <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.75 }}>
                        PNG or JPG · recommended 600 × 180px on transparent/white background.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Email Logo Width (px)"
                      value={(formik.values as any).email_logo_width || 220}
                      onChange={(e) => {
                        const raw = Number(e.target.value) || 220;
                        formik.setFieldValue(
                          "email_logo_width",
                          Math.max(120, Math.min(320, raw))
                        );
                      }}
                      inputProps={{ min: 120, max: 320 }}
                      helperText="Allowed range: 120px – 320px. Height stays automatic to preserve aspect ratio."
                      sx={{ width: 260 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      bgcolor: "#f3f4f6",
                      p: 3,
                      borderRadius: 2,
                      textAlign: "center",
                      border: "1px dashed rgba(15,23,42,0.15)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        color: "text.disabled",
                        mb: 1,
                      }}
                    >
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        py: 3,
                        borderRadius: 2,
                        border: "1px solid rgba(15,23,42,0.06)",
                        maxWidth: 620,
                        mx: "auto",
                      }}
                    >
                      {(() => {
                        const w = Math.max(
                          120,
                          Math.min(320, Number((formik.values as any).email_logo_width) || 220)
                        );
                        const src = selectedEmailLogo
                          ? URL.createObjectURL(selectedEmailLogo)
                          : (formik.values as any).email_logo_source_type === "url"
                            ? (formik.values as any).email_logo_url
                            : emailLogoPath || null;
                        if (!src) {
                          return (
                            <Typography sx={{ fontSize: 12.5, color: "text.disabled", py: 2 }}>
                              No email logo selected — the bundled placeholder logo will be used as a fallback.
                            </Typography>
                          );
                        }
                        return (
                          <img
                            src={src}
                            alt="Email logo preview"
                            style={{
                              display: "block",
                              width: `${w}px`,
                              maxWidth: "100%",
                              height: "auto",
                              margin: "0 auto",
                            }}
                          />
                        );
                      })()}
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 1 }}>
                      This is how the logo will appear at the top of outgoing emails.
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Punch Login Setting
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} p={"24px"}>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Background color
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <FormControl
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: "8px",
                              fontSize: 14,
                            },
                            '& input[type="color"]': {
                              width: 24,
                              height: 24,
                              border: "none",
                              padding: 0,
                              background: "transparent",
                              cursor: "pointer",
                            },
                          }}
                        >
                          <OutlinedInput
                            id="outlined-adornment-color"
                            value={formik.values.punch_login_background_color}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "punch_login_background_color",
                                e.target.value
                              )
                            }
                            endAdornment={
                              <InputAdornment position="end" sx={{ ml: 1 }}>
                                <input
                                  type="color"
                                  value={
                                    formik.values.punch_login_background_color
                                  }
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "punch_login_background_color",
                                      e.target.value
                                    )
                                  }
                                />
                              </InputAdornment>
                            }
                            aria-describedby="outlined-color-helper-text"
                            inputProps={{
                              "aria-label": "punch_login_background_color",
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Font color</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <FormControl
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 200,
                            "& .MuiOutlinedInput-root": {
                              paddingRight: "8px",
                              fontSize: 14,
                            },
                            '& input[type="color"]': {
                              width: 24,
                              height: 24,
                              border: "none",
                              padding: 0,
                              background: "transparent",
                              cursor: "pointer",
                            },
                          }}
                        >
                          <OutlinedInput
                            id="outlined-adornment-color"
                            value={formik.values.punch_login_font_color}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "punch_login_background_color",
                                e.target.value
                              )
                            }
                            endAdornment={
                              <InputAdornment position="end" sx={{ ml: 1 }}>
                                <input
                                  type="color"
                                  value={formik.values.punch_login_font_color}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "punch_login_font_color",
                                      e.target.value
                                    )
                                  }
                                />
                              </InputAdornment>
                            }
                            aria-describedby="outlined-color-helper-text"
                            inputProps={{
                              "aria-label": "punch_login_font_color",
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                          Background Image
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Box className={classes.imgBox}>
                          {selectedImagePunch && (
                            <img
                              src={URL.createObjectURL(selectedImagePunch)}
                              alt="Uploaded img"
                              className={classes.SelectImageBox}
                            />
                          )}
                          {!selectedImagePunch &&
                          initialData &&
                          initialData.punch_login_background_url ? (
                            <img
                              alt="Selected img"
                              height="160px"
                              src={initialData.punch_login_background_url}
                              style={{ position: "inherit" }}
                              className={classes.SelectImageBox}
                            />
                          ) : (
                            <></>
                          )}
                          <div>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="icon-button-file-up-bg_punch"
                              type="file"
                              onChange={handleImageChangePunch}
                            />
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <label htmlFor="icon-button-file-up-bg_punch">
                                <IconButton
                                  color="primary"
                                  aria-label="upload picture up bg"
                                  component="span"
                                >
                                  <CloudUploadIcon sx={{ fontSize: 45 }} />
                                </IconButton>
                              </label>
                              <div>PNG, JPG and JPEG are allowed</div>
                            </div>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Set Slider Items
                    </Typography>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12} pb={5}>
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      <TableContainer>
                        <Table className={classes.table}>
                          <TableHead className={classes.tableHeaderRow}>
                            <TableRow>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "10%" }}
                              >
                                Type
                              </TableCell>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "85%" }}
                              >
                                Add Image / Video
                              </TableCell>
                              <TableCell
                                className={classes.tablheaderId}
                                sx={{ width: "5%", textAlign: "end" }}
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {isLoading ? (
                              <TableRow>
                                <TableCell colSpan={20} align="center">
                                  <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                                </TableCell>
                              </TableRow>
                            ) : isError || punchSlideItems.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={20} align="center">
                                  {isError
                                    ? "Error fetching data"
                                    : "No data available"}
                                </TableCell>
                              </TableRow>
                            ) : (
                              punchSlideItems &&
                              punchSlideItems.map((row, index) => {
                                return (
                                  <TableRow key={index}>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "10%" }}
                                    >
                                      <Autocomplete
                                        options={Object.values(
                                          punchSliderItems
                                        )}
                                        value={row.type || ""}
                                        onChange={(event, newValue) =>
                                          handleFieldChangePB(
                                            index,
                                            "type",
                                            newValue || ""
                                          )
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="type.."
                                            size="small"
                                            className={classes.textField}
                                            InputProps={{
                                              ...params.InputProps,
                                              sx: {
                                                fontSize: 14,
                                                border: "none",
                                                minWidth: "150px",
                                              },
                                              classes: {
                                                root: classes.textFieldQuote23,
                                                notchedOutline:
                                                  classes.notchedOutline,
                                              },
                                            }}
                                            InputLabelProps={{
                                              sx: {
                                                fontSize: 14,
                                                border: "none",
                                              },
                                            }}
                                          />
                                        )}
                                        sx={{ width: "200px" }}
                                        size="small"
                                        className={classes.searchTextField23}
                                      />
                                    </TableCell>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "85%", minWidth: 500 }}
                                    >
                                      <Grid container spacing={1}>
                                        <Grid
                                          item
                                          lg={12}
                                          md={12}
                                          sm={12}
                                          xs={12}
                                        >
                                          {row.type === "image" ? (
                                            <Box className={classes.imgBox}>
                                              {row.url && (
                                                <img
                                                  src={row.url}
                                                  alt="Uploaded preview"
                                                  className={
                                                    classes.SelectImageBox
                                                  }
                                                />
                                              )}
                                              <div>
                                                <input
                                                  type="file"
                                                  accept="image/*"
                                                  id={`upload-${index}`}
                                                  style={{ display: "none" }}
                                                  onChange={(e) =>
                                                    handleFieldChangeImg(
                                                      index,
                                                      e
                                                    )
                                                  }
                                                  className={
                                                    classes.SelectImageBox
                                                  }
                                                />
                                                <div
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <label
                                                    htmlFor={`upload-${index}`}
                                                  >
                                                    <IconButton
                                                      color="primary"
                                                      component="span"
                                                    >
                                                      <CloudUploadIcon
                                                        sx={{ fontSize: 45 }}
                                                      />
                                                    </IconButton>
                                                  </label>
                                                  <div>
                                                    PNG, JPG and JPEG are
                                                    allowed
                                                  </div>
                                                </div>
                                              </div>
                                            </Box>
                                          ) : row.type === "video" ? (
                                            <TextField
                                              placeholder="Video URL"
                                              size="small"
                                              value={row.url}
                                              onChange={(e) =>
                                                handleFieldChangePB(
                                                  index,
                                                  "url",
                                                  e.target.value
                                                )
                                              }
                                              className={classes.textField}
                                              InputProps={{
                                                sx: {
                                                  fontSize: 14,
                                                },
                                              }}
                                              InputLabelProps={{
                                                sx: {
                                                  fontSize: 14,
                                                },
                                              }}
                                            />
                                          ) : null}
                                        </Grid>
                                      </Grid>
                                    </TableCell>
                                    <TableCell
                                      className={classes.tablCellId}
                                      sx={{ width: "5%", textAlign: "end" }}
                                    >
                                      <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <Grid item>
                                          <IconButton
                                            color="error"
                                            onClick={() =>
                                              handleDeleteRowPB(index)
                                            }
                                          >
                                            <FaTrash />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        py={2}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={handleAddRowPB}
                        >
                          Add New
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container spacing={5}></Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Facial Login Setting
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Radius (m)</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Radius (m)"
                          size="small"
                          {...formik.getFieldProps("calculate_radius")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Latitude</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Company Location latitude"
                          size="small"
                          {...formik.getFieldProps("company_latitude")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item lg={3} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">Longitude</Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Company Location longitude"
                          size="small"
                          {...formik.getFieldProps("company_longitude")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* ─────────────────────────────────────────────────────────
                 WHITE-LABEL BRANDING — sections added by the 2026_09_08
                 migration. Each section is independent so admins can fill
                 them in any order; blank values remain null in the DB.
                 ───────────────────────────────────────────────────────── */}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Company Identity</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Extended name fields used throughout the app (headers, emails, invoices).
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Short Name</Typography>
                    <TextField size="small" fullWidth placeholder="e.g. Step Up" {...formik.getFieldProps("company_short_name")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Legal Name</Typography>
                    <TextField size="small" fullWidth placeholder="Full registered name" {...formik.getFieldProps("company_legal_name")} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Country</Typography>
                    <TextField size="small" fullWidth {...formik.getFieldProps("company_country")} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Alternate Email</Typography>
                    <TextField size="small" fullWidth type="email" {...formik.getFieldProps("companyAltEmail")} />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Social Media</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Only icons for URLs you fill in will appear in the app.
                    </Typography>
                  </Grid>
                  {(["facebook", "instagram", "linkedin", "youtube", "tiktok", "twitter"] as const).map((k) => (
                    <Grid item xs={12} md={4} key={k}>
                      <Typography variant="subtitle2" sx={{ textTransform: "capitalize" }}>{k === "twitter" ? "X (Twitter)" : k}</Typography>
                      <TextField size="small" fullWidth placeholder={`https://${k}.com/...`} {...formik.getFieldProps(`${k}_url`)} />
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Brand Assets</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Public URLs or storage-relative paths. Use the existing image upload above for the primary logo; the fields below are for optional additional assets.
                    </Typography>
                  </Grid>
                  {([
                    // [field, label, hint, previewMaxHeight]
                    ["secondary_logo_path",   "Secondary Logo",   "Alternative logo (footers, dark backgrounds)",       100],
                    ["login_logo_path",       "Login Logo",       "Shown on the sign-in screen (falls back to Primary Logo)", 100],
                    ["print_logo_path",       "Print / PDF Logo", "Used in printable invoices, receipts and reports",    100],
                    ["portal_background_url", "Portal Background","Optional background for portal pages",                160],
                  ] as const).map(([field, label, hint, previewH]) => (
                    <Grid item xs={12} md={6} key={field}>
                      <Typography variant="subtitle2">{label}</Typography>
                      <TextField size="small" fullWidth placeholder="URL or path" {...formik.getFieldProps(field)} />
                      <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mb: 1 }}>{hint}</Typography>
                      {/* Live preview — respects the same aspect-ratio-preserving
                          sizing contract as the Primary Logo preview above. */}
                      <LogoPreview
                        src={(formik.values as any)[field] as string}
                        alt={label}
                        maxHeightPx={previewH}
                        maxWidthPx={320}
                      />
                    </Grid>
                  ))}

                  {/* Favicon — dedicated upload block. Unlike the other
                      Brand Assets fields (which are URL-only), the favicon
                      doubles as the app loader identity so most admins
                      will want to drop in an image rather than paste a
                      hosted URL. The URL field is retained below as an
                      "advanced" fallback for tenants who host their
                      favicon on an external CDN. */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Favicon</Typography>
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        border: "1px dashed rgba(15,23,42,0.20)",
                        borderRadius: 1.5,
                        bgcolor: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: 1,
                          border: "1px solid rgba(15,23,42,0.10)",
                          bgcolor: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        {formik.values.favicon_path ? (
                          <img
                            src={formik.values.favicon_path as string}
                            alt="Favicon preview"
                            style={{
                              width: 72,
                              height: 72,
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                            }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            No favicon
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ flex: "1 1 auto", minWidth: 200 }}>
                        <input
                          accept="image/png,image/jpeg,image/jpg,image/gif"
                          style={{ display: "none" }}
                          id="favicon-upload-input"
                          type="file"
                          onChange={handleFaviconChange}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <label htmlFor="favicon-upload-input">
                            <Button
                              variant="outlined"
                              size="small"
                              component="span"
                              startIcon={
                                isFaviconUploading ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CloudUploadIcon />
                                )
                              }
                              disabled={isFaviconUploading}
                            >
                              {formik.values.favicon_path ? "Replace" : "Upload"}
                            </Button>
                          </label>
                          {formik.values.favicon_path && (
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              startIcon={<FaTrash />}
                              onClick={handleFaviconRemove}
                              disabled={isFaviconUploading}
                            >
                              Remove
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 1, lineHeight: 1.6 }}>
                      Browser-tab icon AND app loader identity.
                      Recommended: 512 × 512 px. Minimum: 256 × 256 px.
                      Square PNG with transparent background works best.
                      Use a compact/circular mark with minimal empty padding.
                      Accepted: PNG, JPG, JPEG, GIF (max 2 MB).
                    </Typography>

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Or enter a public URL"
                      sx={{ mt: 1 }}
                      {...formik.getFieldProps("favicon_path")}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Brand Colors</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Hex values (#RGB, #RRGGBB or #RRGGBBAA). Published as CSS variables (<code>--brand-primary</code>, <code>--brand-secondary</code>, <code>--brand-accent</code>) and consumed by the login screen and future themed components.
                    </Typography>
                  </Grid>
                  {[
                    ["brand_primary_color",   "Primary Color"],
                    ["brand_secondary_color", "Secondary Color"],
                    ["brand_accent_color",    "Accent Color"],
                  ].map(([field, label]) => (
                    <Grid item xs={12} md={4} key={field}>
                      <Typography variant="subtitle2">{label}</Typography>
                      <TextField
                        size="small" fullWidth placeholder="#RRGGBB"
                        {...formik.getFieldProps(field)}
                        InputProps={{
                          endAdornment: formik.values[field as keyof typeof formik.values] ? (
                            <InputAdornment position="end">
                              <Box sx={{ width: 22, height: 22, borderRadius: "50%", border: "1px solid #cbd5e1", backgroundColor: String(formik.values[field as keyof typeof formik.values] || "transparent") }} />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>

            {/* ── Admin Notification Emails ──
                 Per-tenant list of addresses that receive ALL admin/system
                 notification emails. When empty, the existing recipient
                 chain (integration → companyEmail → config fallback) is
                 preserved. Chip input reuses the MUI Autocomplete primitive
                 (multiple + freeSolo) — grep confirmed no existing chip-
                 style tag input exists in this repo. */}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Admin Notification Emails
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Email addresses entered here will receive all administrative
                      notification emails for this company.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      freeSolo
                      autoSelect
                      options={[]}
                      value={(formik.values as any).admin_notification_emails || []}
                      onChange={(_e, newVal) => {
                        const seen: Record<string, true> = {};
                        const out: string[] = [];
                        (newVal as string[]).forEach((raw) => {
                          String(raw)
                            .split(/[\s,;]+/)
                            .forEach((tok) => {
                              const norm = tok.trim().toLowerCase();
                              if (!norm || seen[norm]) return;
                              seen[norm] = true;
                              out.push(norm);
                            });
                        });
                        formik.setFieldValue("admin_notification_emails", out);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Type an email and press Enter"
                          helperText={
                            (formik.errors as any).admin_notification_emails ||
                            "Press Enter (or click away) to add. Up to 20 addresses."
                          }
                          error={Boolean((formik.errors as any).admin_notification_emails)}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={2} p={"24px"}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Email Branding</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      From-name and optional footer text used in outgoing emails. The email logo is set above.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Email From Name</Typography>
                    <TextField size="small" fullWidth placeholder='e.g. "Company Support"' {...formik.getFieldProps("email_from_name")} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Email Footer Text</Typography>
                    <TextField size="small" fullWidth multiline minRows={2} {...formik.getFieldProps("email_footer_text")} />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid container spacing={3} p={"24px"}>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="contained"
                      startIcon={
                        settingLoading ? (
                          <CircularProgress
                            size={18}
                            className={classes.isLoading}
                          />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      className={classes.addButton}
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Index;
